/**
 * Treemap : poids relatif des valeurs d'une dimension catégorielle.
 *
 * Porté de Metrix (`features/reports/widgets/TreemapWidget.tsx`), branché sur le
 * socle Orion (`chartStyle`, `fieldCatalog`, `useDrillDown`). Cliquer une case
 * descend d'un niveau ; `nodeClick: false` désactive le zoom natif du treemap
 * pour que le clic serve exclusivement au drill-down.
 *
 * `buildTreemapOption` est une fonction pure, testée sans DOM.
 */
import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { WidgetConfig } from '../schemas';
import type { WidgetQueryResult } from '../useWidgetData';
import { useFieldCatalog, metricLabel, metricFormat, type FieldCatalog } from '../fieldCatalog';
import { CHART_COLORS, TITLE_STYLE } from '../chartStyle';
import { useChartPalette } from '../ChartPaletteContext';
import { resolveWidgetPalette } from '../chartPalette';
import { useDrillDown, type DrillItem } from '../useDrillDown';
import { DrillBreadcrumb } from '../DrillBreadcrumb';
import { aggregateCategoryItems, buildItemTooltip, type TooltipItemParam } from './categoryItems';

// Cadre + padding fournis par WidgetShell (WidgetRenderer) : ici, hauteur + flex.
const SHELL = 'h-full flex flex-col';
const DEFAULT_LIMIT = 20;

interface TreemapNode {
  name: string;
  value: number;
  itemStyle: { color: string };
}

interface TreemapSeries {
  name: string;
  type: 'treemap';
  data: TreemapNode[];
  label: Record<string, unknown>;
  breadcrumb: { show: boolean };
  roam: boolean;
  nodeClick: false;
  leafDepth: number;
  itemStyle: Record<string, unknown>;
  width: string;
  height: string;
  top: number;
}

export interface TreemapOption {
  title?: { text: string } & typeof TITLE_STYLE;
  tooltip: ReturnType<typeof buildItemTooltip>;
  series: TreemapSeries[];
}

/** Options ECharts du treemap (fonction pure — c'est elle qu'on teste). */
export function buildTreemapOption(
  config: WidgetConfig,
  data: WidgetQueryResult | null,
  catalog: FieldCatalog | null,
  drillItems?: DrillItem[] | null,
  palette: string[] = CHART_COLORS,
): TreemapOption {
  const metric = config.metrics?.[0];
  const dimension = config.dimensions?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;
  const limit = (display.limit as number | undefined) ?? DEFAULT_LIMIT;
  const hasTitle = !!config.title;

  const format = metricFormat(catalog, metric ?? '');
  const label = metric ? metricLabel(catalog, metric, config.metric_labels) : '';
  const seriesColors = (config.format?.series_colors as Record<string, string> | undefined) ?? {};

  const items: DrillItem[] =
    drillItems ??
    (metric && dimension && data ? aggregateCategoryItems(data.current, dimension, metric, catalog, limit) : []);

  const option: TreemapOption = {
    title: hasTitle ? { text: config.title as string, ...TITLE_STYLE } : undefined,
    tooltip: buildItemTooltip(format),
    series: [],
  };

  if (!items.length) return option;

  option.series = [
    {
      name: label,
      type: 'treemap',
      data: items.map((item, i) => ({
        name: item.key,
        value: item.value,
        itemStyle: { color: seriesColors[item.key] ?? palette[i % palette.length] },
      })),
      label: {
        show: true,
        fontSize: 11,
        color: '#FFFFFF',
        formatter: (p: TooltipItemParam) => String(p.name ?? ''),
      },
      // Le fil d'Ariane et le zoom natifs du treemap sont désactivés : la navigation
      // passe par `useDrillDown` + `DrillBreadcrumb`, comme sur les autres widgets.
      breadcrumb: { show: false },
      roam: false,
      nodeClick: false,
      leafDepth: 1,
      itemStyle: { borderColor: '#FFFFFF', borderWidth: 2, gapWidth: 2 },
      width: '100%',
      height: '100%',
      top: hasTitle ? 28 : 0,
    },
  ];

  return option;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetQueryResult | null;
  loading?: boolean;
  error?: string;
}

export default function TreemapWidget({ config, data, loading, error }: WidgetProps) {
  // Hooks avant tout early return (règle des hooks).
  const catalog = useFieldCatalog((data?.meta as { source_type?: string } | undefined)?.source_type);
  const palette = resolveWidgetPalette(config.format?.palette as string | string[] | undefined, useChartPalette());

  const metric = config.metrics?.[0];
  const dimension = config.dimensions?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;
  const drillDimensions = (display.drill_dimensions as string[] | undefined) ?? [];
  const period = (data?.meta as { period?: { start: string; end: string } } | undefined)?.period;

  const drill = useDrillDown({
    enabled: drillDimensions.length > 0 && !!config.data_source_id && !!metric && !!dimension && !!period,
    dimension: dimension ?? '',
    drillDimensions,
    dataSourceId: config.data_source_id ?? '',
    metric: metric ?? '',
    topN: (display.limit as number | undefined) ?? DEFAULT_LIMIT,
    period: period ?? { start: '', end: '' },
  });

  const option = useMemo(
    () => buildTreemapOption(config, data, catalog, drill.drillItems, palette),
    [config, data, catalog, drill.drillItems, palette],
  );

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  // Un échec de drill-down (400, timeout…) s'affiche dans l'état d'erreur du widget.
  const shownError = error ?? drill.error;

  if (shownError) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-red-600">{shownError}</span>
      </div>
    );
  }

  if (!data || !config.data_source_id || !metric || !dimension) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  return (
    <div className={SHELL}>
      <DrillBreadcrumb drillPath={drill.drillPath} catalog={catalog} onDrillTo={drill.drillTo} />
      {drill.isLoading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-[#8B8FA7]">Chargement…</div>
      ) : option.series.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm italic text-[#8B8FA7]">Aucune donnée</div>
      ) : (
        <ReactECharts
          option={option}
          notMerge
          style={{ flex: 1, minHeight: 200 }}
          onEvents={
            drill.canDrillDown ? { click: (params: { name: string }) => void drill.handleDrillDown(params.name) } : {}
          }
        />
      )}
    </div>
  );
}
